import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Pill, AlertCircle, Building2, Eye } from 'lucide-react';
import { authApi } from '@/api/auth';
import { clerkSignOut, HUB_URL } from '@/lib/clerkSso';

/**
 * Première connexion d'un titulaire venu de PharmaGestion.
 *
 * Son identité est établie (Clerk), mais aucune pharmacie ne lui est rattachée.
 * C'est le tout premier geste du module RH : décrire son officine. Tant que ce
 * n'est pas fait, il n'y a rien à lui montrer — pas de planning, pas d'équipe,
 * pas de fiche navette.
 *
 * Ni e-mail ni mot de passe ne sont demandés : l'un vient du hub, l'autre n'a
 * pas lieu d'être.
 */
export default function OnboardingPage({ email }: { email: string }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    pharmacyName: '',
    shuttleEmail: '',
    address: '',
    teamPin: '',
  });

  function update<K extends keyof typeof data>(key: K, value: string) {
    setData((d) => ({ ...d, [key]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await authApi.onboard({
        pharmacyName: data.pharmacyName,
        shuttleEmail: data.shuttleEmail,
        address: data.address || undefined,
        teamPin: data.teamPin,
      });
      // Le compte existe désormais : `/api/auth/me` répondra, et RootRedirect
      // enverra sur le tableau de bord.
      await qc.invalidateQueries();
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError((err as Error).message || 'Création impossible');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="w-full min-h-screen bg-slate-50 flex flex-col">
      <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-lg bg-accent flex items-center justify-center">
            <Pill size={14} color="#fff" strokeWidth={2.25} />
          </div>
          <div className="text-[14px] font-semibold">Officina</div>
        </div>
        <div className="flex items-center gap-3 text-[11px] text-slate-500">
          <span>{email}</span>
          <button
            onClick={() => {
              void clerkSignOut().then(() => {
                window.location.href = HUB_URL;
              });
            }}
            className="hover:text-slate-900 underline"
          >
            Se déconnecter
          </button>
        </div>
      </div>

      <div className="flex-1 flex items-start justify-center p-8 pt-16">
        <div className="w-full max-w-[560px]">
          <div className="mb-6">
            <div className="w-11 h-11 rounded-xl bg-accent/10 text-accent flex items-center justify-center mb-4">
              <Building2 size={21} strokeWidth={2} />
            </div>
            <h1 className="text-[30px] font-semibold tracking-tight text-slate-900 leading-tight">
              Votre officine
            </h1>
            <p className="text-[14px] text-slate-500 mt-2 leading-relaxed">
              Bienvenue sur le module RH. Avant tout, décrivez votre pharmacie — planning,
              équipe et fiche navette s'appuient dessus. Vous pourrez tout modifier ensuite
              dans les Réglages.
            </p>
          </div>

          {/* Le piège de l'accès en consultation : le titulaire d'une officine
              dont un autre gère le RH arrive ici tant que l'accès n'est pas
              ouvert. S'il remplit le formulaire, son adresse est prise par sa
              propre pharmacie, vide, et l'accès ne peut plus être ouvert. */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4 flex items-start gap-2.5 text-[12.5px] text-slate-700 leading-relaxed">
            <Eye size={15} className="mt-0.5 flex-shrink-0 text-amber-600" />
            <div>
              <strong className="text-slate-900">
                Vous venez voir le planning d'une officine dont un autre titulaire gère le RH ?
              </strong>{' '}
              Ne remplissez rien ici. Demandez-lui de vous ouvrir un{' '}
              <strong>accès en consultation</strong> (Paramètres → Accès consultation) avec
              l'adresse <strong>{email}</strong>, puis reconnectez-vous.
            </div>
          </div>

          <form
            onSubmit={handleSubmit}
            className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4"
          >
            <div>
              <label className="label">Nom de la pharmacie</label>
              <input
                className="input"
                value={data.pharmacyName}
                onChange={(e) => update('pharmacyName', e.target.value)}
                placeholder="Pharmacie de la République"
                autoFocus
                required
                minLength={2}
              />
            </div>

            <div>
              <label className="label">Adresse</label>
              <input
                className="input"
                value={data.address}
                onChange={(e) => update('address', e.target.value)}
                placeholder="12 rue de la République, 75011 Paris"
              />
            </div>

            <div>
              <label className="label">E-mail de la fiche navette</label>
              <input
                type="email"
                className="input"
                value={data.shuttleEmail}
                onChange={(e) => update('shuttleEmail', e.target.value)}
                placeholder="cabinet@mon-comptable.fr"
                required
              />
              <div className="text-[11px] text-slate-400 mt-1">
                L'adresse à laquelle part le récapitulatif mensuel des heures — en général
                celle de votre comptable.
              </div>
            </div>

            <div>
              <label className="label">Code PIN de l'équipe</label>
              <input
                className="input"
                value={data.teamPin}
                onChange={(e) => update('teamPin', e.target.value.replace(/\D/g, '').slice(0, 4))}
                placeholder="4 chiffres"
                inputMode="numeric"
                required
                minLength={4}
                maxLength={4}
              />
              <div className="text-[11px] text-slate-400 mt-1">
                Ouvre la vue du jour sur un poste partagé (comptoir, back office), sans
                accès aux données RH. Modifiable à tout moment.
              </div>
            </div>

            {error && (
              <div className="bg-danger-bg border border-danger/30 rounded-lg p-2.5 flex items-start gap-2 text-[11.5px] text-danger">
                <AlertCircle size={13} className="mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary hover:bg-primary-hover text-white font-medium text-[13px] py-2.5 rounded-lg transition-colors disabled:opacity-50"
            >
              {loading ? 'Création…' : 'Créer mon officine'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
